import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Immutable from 'immutable';
import { Helmet } from 'react-helmet';

import {
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
  setOperationStatus,
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import { Container, Button } from '@uncinc/uncinc-react-kitchen-sink';
import { useHistory, useLocation } from 'react-router-dom';

import StepsProgressBar from '../../../../components/StepsProgressBar';
import CartProducts from '../CartProducts';
import CartSummary from '../../CartSummary';
import { createOrUpdateUserProfile } from '../../../../store/actions/Profile';
import { commerceCartFinishOrder } from '../../../../store/actions/Commerce';
import { formatCartItemsForAnalytics } from '../../../../helpers/commerce';
import { clearPageInformation, setPageInformation, setPageViewSent, createOrUpdateNewsletterContactSubscription } from '../../../../store/actions/UI';

import { DEFAULT_LANGUAGE } from '../../../../config/constants';
import { prepareSpotlerListData } from '../../../../helpers';

import './style.scss';

const CartCheckoutConfirm = () => {
  const PAGE_TITLE = 'Afrekenen | VeiligheidNL';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  // User data.
  const uid = useSelector((state) => state.getIn(['login', 'uid'], 0));
  const userMail = useSelector((state) => state.getIn(['user', uid, 'mail', 0, 'value'], null));

  // Profile data.
  const profileFirstName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_first_name', 0, 'value'], null));
  const profileInfixName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_infix_name', 0, 'value'], null));
  const profileLastName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_last_name', 0, 'value'], null));
  const profileOrganisation = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_organisation_name', 0, 'value'], null));
  const profileWorkArea = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_work_area', 0, 'value'], null));
  const profileJobTitle = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_job_title', 0, 'value'], null));

  // Spotler options.
  const workFieldList = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list1'], null)));
  const jobTitleList = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list2'], null)));

  const commerce = useSelector((state) => state.get('commerce'));
  const totalPrice = useSelector((state) => state.getIn(['commerce', 'cart', 'content', 'attributes', 'order_total', 'subtotal', 'number']));
  const adjustments = useSelector((state) => state.getIn(['commerce', 'cart', 'content', 'attributes', 'order_total', 'adjustments'], Immutable.List()));
  const taxObj = adjustments.filter((adjustment) => adjustment.get('type') === 'tax');
  const taxAmount = taxObj.getIn([0, 'total', 'number'], 0);
  const shippingObj = adjustments.filter((adjustment) => adjustment.get('type') === 'shipping');
  const shippingAmount = shippingObj.getIn([0, 'total', 'number'], 0);

  const analyticsItems = useSelector((state) => formatCartItemsForAnalytics(state.getIn(['commerce', 'cart', 'products'])));

  const checkoutStatus = useSelector((state) => state.getIn(['ui', 'commerce', 'operations', 'cart_checkout', 'status']));
  const profileUpdateStatus = useSelector((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status']));
  const isLoading = (profileUpdateStatus === UI_OPERATION_STATUS_REQUEST && checkoutStatus === UI_OPERATION_STATUS_REQUEST);

  const location = useLocation();
  const currentPath = location.pathname;
  const [agreed, setAgreed] = useState(false);
  const [agreedError, setAgreedError] = useState(null);
  const [newsletter, setNewsletter] = useState(false);
  const shoppingCartProducts = useSelector((state) => state.getIn(['commerce', 'cart', 'products']));
  const orderNumber = useSelector((state) => state.getIn(['uiInternal', 'prevOrderNumber'], ''));

  const sendOrderPurchaseToAnalytics = () => {
    if (window) {
      window.dataLayer = window.dataLayer || [];

      // send analytics event
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          tax: taxAmount,
          shipping: shippingAmount,
          transaction_id: orderNumber,
          currency: 'EUR',
          value: totalPrice,
          items: analyticsItems,
        },
      });
    }
  };

  useEffect(() => {
    if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS && checkoutStatus === UI_OPERATION_STATUS_SUCCESS) {
      sendOrderPurchaseToAnalytics();
      history.push('/cart/checkout/success');
      dispatch(setOperationStatus('profile', 'update', null));
      dispatch(setOperationStatus('commerce', 'cart_checkout', null));
    }
  }, [dispatch, history, profileUpdateStatus, checkoutStatus, shoppingCartProducts]);

  useEffect(() => {
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmCheckout = () => {
    if (!agreed) {
      setAgreedError(true);
      return;
    }

    dispatch(commerceCartFinishOrder());


    dispatch(createOrUpdateUserProfile({
      field_subscribe_to_newsletter: [{ value: newsletter }],
    }));

    if (newsletter) {
      // If we are logged-in, add extra profile data to the Spotler contact.
      let extraProfileData = {};
      // Prepare list data.
      const spotlerListData = prepareSpotlerListData(
        { fields: workFieldList, selected: profileWorkArea },
        { fields: jobTitleList, selected: profileJobTitle },
      );
      extraProfileData = {
        email: userMail,
        firstName: profileFirstName,
        lastName: profileInfixName ? `${profileInfixName} ${profileLastName}}` : profileLastName,
        organisation: profileOrganisation,
        ...spotlerListData,
      };

      // Create or update our Spotler newsletter subscription.
      dispatch(createOrUpdateNewsletterContactSubscription(extraProfileData));
    }

    if (window) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'webshop.order.finalize',
      });

      window.dataLayer.push({
        event: 'webshop.order.subscribe_to_newsletter',
        value: newsletter,
      });

      commerce.getIn(['cart', 'products'], Immutable.List()).map((product) => {
        const title = product.getIn(['relationships', 'purchased_entity', 'relationships', 'product_id', 'attributes', 'title'], product.getIn(['attributes', 'title'], ''));
        const price = product.getIn(['attributes', 'total_price', 'number']);
        const qty = parseInt(product.getIn(['attributes', 'quantity'], 1));
        const sku = product.getIn(['relationships', 'purchased_entity', 'attributes', 'sku']);

        window.dataLayer.push({
          event: 'webshop.product.order',
          productName: title,
          productPrice: price,
          productQuantity: qty,
          productSku: sku,
        });
      });
    }
  };

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <StepsProgressBar webShopCurrentPath={currentPath} />
      <Container className="CartStep3">
        <div className="Cart__backToPage" onClick={() => history.goBack()}>
          <div className="icon icon-chevron-right" />
          {t('cart.back')}
        </div>
        <div className="Cart__container">
          <div className="Cart__content">
            <h5 className="Cart__title">{t('cart.order_overview')}</h5>
            <CartProducts readOnly />
          </div>

          <div className="CartSummary">
            <div className="CartSummary__wrapper">
              <div className="CartSummary__title">
                <strong>{t('cart.your_order')}</strong>
              </div>

              <CartSummary />
            </div>

            <div className="CartSummary__wrapper payment">
              <i className="icon icon-check-radio icon-variant-2" /> {t('cart.pay_with_invoice')}
            </div>

            <div className="CartSummary__agree">
              <i
                onClick={() => setAgreed((a) => !a)}
                className={`icon icon-check-radio ${!agreed && 'icon-variant-1'}`}
              />
              {t('cart.agree_with')}&nbsp;
              <a href="https://www.veiligheid.nl/algemene-voorwaarden" target="_blank" rel="noreferrer">
                {t('cart.terms')}
              </a>
              *
              {agreedError && <div className="FormElement__error TextField__error">{t('webform.required')}</div>}
            </div>

            <div className="CartSummary__agree"> <i onClick={() => setNewsletter((n) => !n)} className={`icon icon-check-radio ${!newsletter && 'icon-variant-1'}`} />{t('cart.sign_to_newsletter')}</div>

            {shoppingCartProducts.size < 1 ? (
              <Button
                disabled
                className={`Button Button--primary ${isLoading ? 'Button--loading' : ''}`}
              >
                {t('cart.place_order')} <span className="icon icon-arrow-right icon-variant-2" />
              </Button>
            ) : (
              <Button
                onClick={confirmCheckout}
                className={`Button Button--primary ${isLoading ? 'Button--loading' : ''}`}
              >
                {t('cart.place_order')} <span className="icon icon-arrow-right icon-variant-2" />
              </Button>
            )}


            {(profileUpdateStatus === UI_OPERATION_STATUS_ERROR || checkoutStatus === UI_OPERATION_STATUS_ERROR) && (
              <div className="Form__error">Er is iets mis gegaan. Probeer het later nog eens.</div>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default CartCheckoutConfirm;
